import { useState, useEffect } from 'react';
import { client } from '../../sdkAdapter';
import Swal from 'sweetalert2';
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import OrderDetailsModal from "../ecommerce/OrderDetailsModal";
import OrderCreateModal from "../ecommerce/OrderCreateModal";
import Badge from "../ui/badge/Badge";
import Pagination from "../ui/pagination";

export default function OrdersTable() {
    const [orders, setOrders] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const fetchOrders = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await client.getApiOrdersOrders() as any;

            // Try multiple extraction paths
            let data = [];
            if (res && typeof res === 'object') {
                if (Array.isArray(res)) {
                    data = res;
                } else if (Array.isArray(res.data)) {
                    data = res.data;
                } else if (res.data && Array.isArray(res.data.data)) {
                    data = res.data.data;
                } else if (res.data && Array.isArray(res.data.data?.data)) {
                    data = res.data.data.data;
                }
            }

            setOrders(Array.isArray(data) ? data : []);
        } catch (err: any) {
            console.error('Failed to fetch orders:', err);
            setError(err?.message || 'Failed to fetch orders');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    const totalItems = orders.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));
    const paginatedOrders = orders.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const handleViewOrder = (id: number) => {
        setSelectedOrderId(id);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedOrderId(null);
        fetchOrders();
    };

    const handleDelete = async (id: number) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (!result.isConfirmed) return;
        try {
            await client.postApiOrdersOrderDelete({
                postApiOrdersOrderDeleteRequest: { id }
            });
            fetchOrders();
        } catch (err: any) {
            console.error('Failed to delete order:', err);
            Swal.fire({
                title: 'Error',
                text: 'Failed to delete order',
                icon: 'error'
            });
        }
    };

    return (
        <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Orders List</h3>
                <div className="flex gap-2">
                    <button
                        onClick={fetchOrders}
                        disabled={loading}
                        className="text-sm text-brand-500 hover:text-brand-600 disabled:opacity-50"
                    >
                        {loading ? 'Refreshing...' : 'Refresh'}
                    </button>
                    <button
                        onClick={() => setIsCreateOpen(true)}
                        className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors text-sm"
                    >
                        Create Order
                    </button>
                </div>
            </div>

            {error && <div className="p-4 text-red-500">{error}</div>}

            <div className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableCell isHeader className="px-5 py-3 text-start">Order ID</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Date</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Customer</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Items</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Status</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Total</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-end">Actions</TableCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {orders.length === 0 && !loading ? (
                            <TableRow><TableCell colSpan={7} className="px-5 py-4 text-center">No orders found.</TableCell></TableRow>
                        ) : (
                            paginatedOrders.map((order) => (
                                <TableRow key={order.id}>
                                    <TableCell className="px-5 py-4">#{order.id}</TableCell>
                                    <TableCell className="px-5 py-4 text-gray-500">
                                        {new Date(order.created_at).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell className="px-5 py-4">
                                        <div className="flex flex-col">
                                            <span className="font-medium text-gray-900 dark:text-white">
                                                {order.users ? `${order.users.first_name || ''} ${order.users.last_name || ''}`.trim() || order.users.username : 'Unknown'}
                                            </span>
                                            <span className="text-xs text-gray-500">{order.users?.email}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-5 py-4">
                                        {order.products?.length || 0} items
                                    </TableCell>
                                    <TableCell className="px-5 py-4">
                                        {(() => {
                                            const status = order.status || order.statuses?.[0]?.name || 'Pending';
                                            return (
                                                <Badge
                                                    size="sm"
                                                    color={
                                                        status === "Delivered" || status === "Complete" || status === "Shipped"
                                                            ? "success"
                                                            : status === "Pending"
                                                                ? "warning"
                                                                : status === "Cancelled"
                                                                    ? "error"
                                                                    : "primary"
                                                    }
                                                >
                                                    {status}
                                                </Badge>
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-gray-900 dark:text-white font-medium">
                                        ${order.total || order.amount || '0.00'}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-end">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => handleViewOrder(order.id)}
                                                className="text-gray-500 hover:text-brand-500 dark:text-gray-400 dark:hover:text-brand-400"
                                                title="View/Edit Order"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => handleDelete(order.id)}
                                                className="text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"
                                                title="Delete Order"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {!loading && totalItems > 0 ? (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(rows) => {
                        setRowsPerPage(rows);
                        setCurrentPage(1);
                    }}
                    totalItems={totalItems}
                />
            ) : null}

            <OrderDetailsModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                orderId={selectedOrderId}
            />

            <OrderCreateModal
                isOpen={isCreateOpen}
                onClose={() => setIsCreateOpen(false)}
                onSuccess={fetchOrders}
            />
        </div>
    );
}
